import {
  FaUtensils,
  FaCoffee,
  FaCocktail,
  FaTree,
  FaUmbrellaBeach,
  FaLandmark,
  FaTheaterMasks,
  FaShoppingBag,
  FaShoppingBasket,
  FaSpa,
  FaPills,
  FaBus,
  FaBed,
  FaMapMarkerAlt,
  FaHome,
} from "react-icons/fa";

// Map pins need to be recognisable at a glance, so each guide gets an icon and
// colour from its category. Guides carry no category field of their own, only
// the host's catalog name ("Restaurants", "Things to do") and the place title,
// so the category is inferred from those words. The catalog is checked first —
// it's the host's deliberate label — and the title is the fallback.
//
// Order matters: the first match wins, so narrower categories (cafe, beach)
// sit above broader ones (food, nature).

const words = (list) => new RegExp(`\\b(?:${list.join("|")})\\b`, "i");

const CATEGORIES = [
  {
    key: "cafe",
    label: "Cafe",
    icon: FaCoffee,
    color: "#A0674B",
    match: words(["cafe", "cafes", "café", "coffee", "bakery", "bakeries", "tea", "espresso", "dessert", "desserts", "pastry", "donut", "donuts", "ice cream", "gelato"]),
  },
  {
    key: "bar",
    label: "Bar",
    icon: FaCocktail,
    color: "#D53F8C",
    match: words(["bar", "bars", "pub", "pubs", "nightlife", "cocktail", "cocktails", "brewery", "breweries", "wine", "club", "clubs", "lounge", "tavern", "distillery"]),
  },
  {
    key: "food",
    label: "Food",
    icon: FaUtensils,
    color: "#ED8936",
    match: words(["restaurant", "restaurants", "dining", "food", "eat", "eats", "brunch", "breakfast", "lunch", "dinner", "sushi", "pizza", "steak", "burger", "burgers", "bbq", "barbecue", "grill", "bistro", "diner", "kitchen", "ramen", "taco", "tacos", "seafood"]),
  },
  {
    key: "beach",
    label: "Beach",
    icon: FaUmbrellaBeach,
    color: "#00A3C4",
    match: words(["beach", "beaches", "surf", "swim", "swimming", "waterfront"]),
  },
  {
    key: "nature",
    label: "Outdoors",
    icon: FaTree,
    color: "#38A169",
    match: words(["park", "parks", "nature", "hike", "hikes", "hiking", "trail", "trails", "garden", "gardens", "outdoor", "outdoors", "lake", "forest", "waterfall", "viewpoint", "scenic"]),
  },
  {
    key: "entertainment",
    label: "Entertainment",
    icon: FaTheaterMasks,
    color: "#D69E2E",
    match: words(["entertainment", "theatre", "theater", "cinema", "movie", "movies", "concert", "concerts", "music", "show", "shows", "arcade", "casino", "zoo", "aquarium", "amusement", "bowling", "activities", "activity", "sports", "stadium", "things to do"]),
  },
  {
    key: "sights",
    label: "Sights",
    icon: FaLandmark,
    color: "#9F7AEA",
    match: words(["museum", "museums", "gallery", "galleries", "art", "history", "historic", "landmark", "landmarks", "attraction", "attractions", "sights", "sightseeing", "monument", "castle", "tour", "tours", "temple", "church", "cathedral"]),
  },
  {
    key: "grocery",
    label: "Grocery",
    icon: FaShoppingBasket,
    color: "#319795",
    match: words(["grocery", "groceries", "supermarket", "convenience", "liquor"]),
  },
  {
    key: "shopping",
    label: "Shopping",
    icon: FaShoppingBag,
    color: "#3182CE",
    match: words(["shop", "shops", "shopping", "mall", "malls", "store", "stores", "market", "markets", "boutique", "boutiques", "outlet", "souvenir", "souvenirs"]),
  },
  {
    key: "wellness",
    label: "Wellness",
    icon: FaSpa,
    color: "#F687B3",
    match: words(["spa", "spas", "massage", "gym", "gyms", "fitness", "yoga", "wellness", "salon", "salons"]),
  },
  {
    key: "health",
    label: "Health",
    icon: FaPills,
    color: "#E53E3E",
    match: words(["pharmacy", "pharmacies", "hospital", "clinic", "clinics", "doctor", "urgent care", "medical", "dentist"]),
  },
  {
    key: "transport",
    label: "Transport",
    icon: FaBus,
    color: "#718096",
    match: words(["airport", "station", "transit", "subway", "bus", "train", "taxi", "parking", "car rental", "gas"]),
  },
  {
    key: "lodging",
    label: "Stay",
    icon: FaBed,
    color: "#667EEA",
    match: words(["hotel", "hotels", "hostel", "motel", "lodging"]),
  },
];

export const DEFAULT_CATEGORY = {
  key: "place",
  label: "Place",
  icon: FaMapMarkerAlt,
  color: "#817EFF",
};

// The property is the anchor of the whole map, so it gets a treatment no
// category shares: a white pin with a home glyph.
export const PROPERTY_CATEGORY = {
  key: "property",
  label: "Your stay",
  icon: FaHome,
  color: "#FFFFFF",
};

const findCategory = (text) => {
  if (typeof text !== "string" || !text) return null;
  return CATEGORIES.find((category) => category.match.test(text)) || null;
};

/**
 * Picks the icon/colour for a guide from its catalog name, falling back to
 * its title. Always returns a category, never null.
 */
export const getGuideCategory = (guide) =>
  findCategory(guide?.catalog?.name) ||
  findCategory(guide?.title) ||
  DEFAULT_CATEGORY;
